// Turning 2-step on, step by step: 1 get an authenticator app · 2 scan the QR code (or type the key) · 3 type the
// 6-digit code it shows. The recovery codes (step 4) are shown by the caller from the confirmed answer. The QR
// code is drawn in the browser (the key never goes to a QR website).
import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { ShieldCheck, Smartphone } from 'lucide-react'
import { CopyButton, OTPInput } from '@the-kit/ui'
import type { Session } from '@/api/schemas'
import { ErrorText } from '@/components/ErrorText'
import { useStableCallback } from '@/lib/useStableCallback'
import { useConfirmTwoStep, useTwoStepKey } from '../hooks'

type Step = 1 | 2 | 3

export function TwoStepSetupPanel({ onConfirmed }: { onConfirmed: (session: Session) => void }) {
  const key = useTwoStepKey()
  const confirm = useConfirmTwoStep()
  const [step, setStep] = useState<Step>(1)
  const [qr, setQr] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const { mutate: askForKey } = key
  useEffect(() => askForKey(), [askForKey])
  useEffect(() => {
    if (!key.data) return
    QRCode.toString(key.data.otpauth_uri, { type: 'svg', margin: 1 })
      .then((svg) => setQr('data:image/svg+xml;utf8,' + encodeURIComponent(svg)))
      .catch(() => setQr(null))
  }, [key.data])
  const submit = useStableCallback((typed: string = code) => {
    if (typed.length !== 6 || confirm.isPending) return
    confirm.mutate({ code: typed }, { onSuccess: onConfirmed, onError: () => setCode('') })
  })

  return (
    <div className="space-y-4" data-testid="two-step-setup">
      <p className="eyebrow">Step {step} of 4</p>
      {step === 1 && (
        <div className="space-y-3">
          <div className="card flex items-start gap-3">
            <Smartphone size={18} className="mt-0.5 shrink-0 text-primary" />
            <p className="text-xs text-default">
              Install <b>Google Authenticator</b> or <b>Microsoft Authenticator</b> on your phone (free, from
              the app store). It shows a new 6-digit code every 30 seconds; you type it after your password.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary w-full"
            onClick={() => setStep(2)}
            data-testid="setup-next"
          >
            I have the app
          </button>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-3">
          <div className="card flex flex-col items-center gap-3">
            {qr ? (
              <img src={qr} width={180} height={180} alt="QR code for the authenticator app" />
            ) : (
              <div className="skeleton h-[180px] w-[180px]" />
            )}
            <p className="help">In the app: Add (+) → Scan a QR code. Or add it by hand with this key:</p>
            <code className="mono select-all break-all text-center text-sm" data-testid="setup-key">
              {key.data?.secret ?? '…'}
            </code>
            {key.data && <CopyButton text={key.data.secret} label="Copy key" />}
          </div>
          <ErrorText error={key.error} />
          <button
            type="button"
            className="btn btn-primary w-full"
            disabled={!key.data}
            onClick={() => setStep(3)}
            data-testid="setup-next"
          >
            Added, next
          </button>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-3">
          <p className="text-xs text-default">Type the 6-digit code the app shows for Firstfund Campaigns.</p>
          <div className="flex justify-center py-2">
            <OTPInput value={code} onChange={setCode} onComplete={submit} error={!!confirm.error} autoFocus />
          </div>
          <ErrorText error={confirm.error} />
          <div className="flex gap-2">
            <button type="button" className="btn btn-ghost" onClick={() => setStep(2)}>
              Back
            </button>
            <button
              type="button"
              className="btn btn-primary flex-1"
              disabled={code.length !== 6 || confirm.isPending}
              onClick={() => submit()}
              data-testid="setup-confirm"
            >
              <ShieldCheck size={14} /> Turn on
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
